import React from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { ErrorFlag } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { ManageTranslationsPage } from '@atlassian/jira-language-settings/src/ui/main.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import messages from './messages';

type Props = {
	projectContext: ProjectContext | null;
	tenantContext: TenantContext;
	currentPath: string;
};

export const ServiceDeskLanguageTranslations = (props: Props) => {
	const { formatMessage } = useIntl();

	const { tenantContext, projectContext } = props;

	if (!projectContext || !tenantContext) {
		return <Spinner />;
	}
	const { projectKey, projectType, projectName, projectId } = projectContext;
	const href = `${getNextgenSettingsUrl(projectType, projectKey)}/language`;

	return (
		<>
			<ServiceDeskDocumentTitle projectName={projectName} section="languageTranslations" />
			<AppContainer baseUrl="" projectKey={projectKey} fullSize={getWillShowNav4()}>
				<Breadcrumb
					breadcrumb={{
						text: formatMessage(messages.breadcrumbs),
						href,
					}}
				/>
				<ManageTranslationsPageContainer data-testid="spa-apps-project-settings-service-desk-language-translations.manage-translations-page-container">
					<JSErrorBoundary
						id="ManageTranslationsPage"
						packageName="jiraSpaAppsProjectSettingsServiceDeskLanguageTranslations"
						teamName="jsm-nimbus"
						withExperienceTracker
						sendToPrivacyUnsafeSplunk
						fallback={() => {
							GlobalPageLoadExperience.failure({ force: true });
							return (
								<ErrorFlag
									id="manage-translations-page-error"
									title={formatMessage(messages.errorTitle)}
									description={formatMessage(messages.errorDescription)}
								/>
							);
						}}
					>
						<ManageTranslationsPage projectId={projectId.toString()} projectKey={projectKey} />
					</JSErrorBoundary>
				</ManageTranslationsPageContainer>
			</AppContainer>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ManageTranslationsPageContainerNew = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '120px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ManageTranslationsPageContainerOld = styled.div({
	marginTop: `${token('space.300', '24px')}`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '120px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.aui-nav.aui-nav-breadcrumbs': {
		display: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.translation-row.aui-group:first-of-type': {
		marginTop: token('space.100', '8px'),
	},
});

const ManageTranslationsPageContainer = componentWithCondition(
	getWillShowNav4,
	ManageTranslationsPageContainerNew,
	ManageTranslationsPageContainerOld,
);
